import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CalendarIcon from "../../../assets/icons/CalendarIcon";
import PercentageIcon from "../../../assets/icons/PercentageIcon";
import TitleBar from "../../../components/common/TitleBar";
import WithLoader from "../../../components/WithLoader";
import {
  useCreateInvoiceMutation,
  useGetBusinessUnitListQuery,
  useGetDepartmentListQuery,
  useGetInvoiceDetailsQuery,
  useUpdateInvoiceMutation,
} from "../../../features/Invoice/invoice.api";
import {
  useGetVendorDetailsQuery,
  useGetVendorListQuery,
} from "../../../features/Vendor/vendor.api";
import { BankPayment, MfsPayment } from "../../../features/Vendor/vendor.type";
import useError from "../../../hooks/useError";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  formatNumberWithComma,
  parseNumberFromComma,
} from "../../../utils/number";
import { selectArrayOption, selectSearchOption } from "../../../utils/utils";
import {
  validatePositiveNumber,
  validateReferenceId,
} from "../../../utils/validators";
import InvoiceDocuments from "./_components/ManageInvoice/InvoiceDocuments";

// Extra Fields
/**
 * bank_name
 * payment_to_pay_type
 */

const ManageInvoice = () => {
  const { id } = useParams();
  const isUpdating = !!id;
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // ------ Watched values ------
  const watchedVatCondition = Form.useWatch("vat_condition", form);
  const watchedTdsCondition = Form.useWatch("tds_condition", form);

  const watchedVatPercentage = Form.useWatch("vat_percentage", form);
  const watchedTdsPercentage = Form.useWatch("tds_percentage", form);
  const vatPercentage = Number(watchedVatPercentage || 0);
  const tdsPercentage = Number(watchedTdsPercentage || 0);

  const watchedInvoiceAmount = Form.useWatch("invoice_amount", form);
  const watchedVendorID = Form.useWatch("vendor_id", form);
  // ------ Mutations ------
  const [
    createInvoice,
    {
      isLoading: creating,
      isSuccess: created,
      isError: isCreateError,
      error: createError,
    },
  ] = useCreateInvoiceMutation();
  const [
    updateInvoice,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateInvoiceMutation();

  // ------ Queries ------
  const { data: vendorList } = useGetVendorListQuery({
    params: { status: "active" },
  });

  const findVendor = vendorList?.data?.vendors?.find(
    (vendor) => vendor.id === watchedVendorID,
  );

  const {
    data: vendorData,
    isLoading,
    isFetching,
  } = useGetVendorDetailsQuery(watchedVendorID, {
    skip: !watchedVendorID,
  });

  const { data: businessUnistList } = useGetBusinessUnitListQuery({});
  const { data: departmentList } = useGetDepartmentListQuery({});

  const {
    data: invoiceDetails,
    isLoading: invoiceLoading,
    isFetching: invoiceFetching,
  } = useGetInvoiceDetailsQuery(id as unknown as number, {
    skip: !id,
  });

  const paymentOptions = vendorData?.data?.payments?.map((info) => ({
    value: info.id,
    label:
      (info as BankPayment)?.bankAccount?.bank_name ||
      (info as MfsPayment)?.mfsAccount?.mfs_type,
  }));

  const defaultPaymentId = vendorData?.data?.payments?.find(
    (info) => info.is_default,
  )?.id;

  // ------ Function for form submission handle ------
  const handleFinish = (values: any) => {
    const {
      vat_amount,
      tds_amount,
      payment_amount,
      cost_of_company,
      invoice_amount,
      vat_percentage,
      tds_percentage,
      ...rest
    } = values || {};
    const invoiceData = {
      vat_amount: Number(vat_amount),
      tds_amount: Number(tds_amount),
      payment_amount: Number(payment_amount),
      cost_of_company: Number(cost_of_company),
      invoice_amount: Number(invoice_amount),
      vat_percentage: Number(vat_percentage),
      tds_percentage: Number(tds_percentage),
      ...rest,
    };

    if (isUpdating) {
      updateInvoice({
        data: invoiceData,
        id,
      });
    } else {
      createInvoice(invoiceData);
    }
  };

  const handlePercentageChange = (
    value: string | number,
    type: "vat" | "tds",
  ) => {
    if (!value) {
      if (type === "vat" && !tdsPercentage) {
        if (!watchedVatCondition && !watchedTdsCondition) {
          form.resetFields(["vat_condition", "tds_condition"]);
        }
      } else if (type === "tds" && !vatPercentage) {
        if (!watchedVatCondition && !watchedTdsCondition) {
          form.resetFields(["vat_condition", "tds_condition"]);
        }
      }
    }
  };

  useEffect(() => {
    // if (
    //   watchedInvoiceAmount &&
    //   watchedVatCondition &&
    //   watchedVatPercentage &&
    //   watchedTdsCondition &&
    //   watchedTdsPercentage
    // ) {
    const invoiceAmount = parseFloat(watchedInvoiceAmount) || 0;
    const vatPercentage = parseFloat(watchedVatPercentage) || 0;
    const tdsPercentage = parseFloat(watchedTdsPercentage) || 0;

    let vatAmount = 0;
    let tdsAmount = 0;
    let vendorPayment = invoiceAmount;
    let costOfCompany = invoiceAmount;

    // Case 1: No VAT or TDS
    if (
      (vatPercentage === 0 || !vatPercentage) &&
      (tdsPercentage === 0 || !tdsPercentage)
    ) {
      vatAmount = 0;
      tdsAmount = 0;
      vendorPayment = invoiceAmount;
      costOfCompany = invoiceAmount;
    }
    // Exclusive VAT + Exclusive TDS
    if (
      watchedVatCondition === "exclusive" &&
      watchedTdsCondition === "exclusive"
    ) {
      vatAmount = (invoiceAmount * vatPercentage) / (100 - tdsPercentage);
      tdsAmount = (invoiceAmount * tdsPercentage) / (100 - tdsPercentage);
      vendorPayment = invoiceAmount;
      costOfCompany = invoiceAmount + vatAmount + tdsAmount;
    }

    // Exclusive VAT + Inclusive TDS
    if (
      watchedVatCondition === "exclusive" &&
      watchedTdsCondition === "inclusive"
    ) {
      vatAmount = (invoiceAmount * vatPercentage) / 100;
      tdsAmount = (invoiceAmount * tdsPercentage) / 100;
      vendorPayment = invoiceAmount - tdsAmount;
      costOfCompany = invoiceAmount + vatAmount;
    }

    // Inclusive VAT + Inclusive TDS
    if (
      watchedVatCondition === "inclusive" &&
      watchedTdsCondition === "inclusive"
    ) {
      vatAmount = (invoiceAmount * vatPercentage) / (100 + vatPercentage);
      tdsAmount = (invoiceAmount * tdsPercentage) / (100 + vatPercentage);
      vendorPayment = invoiceAmount - vatAmount - tdsAmount;
      costOfCompany = invoiceAmount;
    }

    // Inclusive VAT + Exclusive TDS → Not acceptable
    if (
      watchedVatCondition === "inclusive" &&
      watchedTdsCondition === "deductible"
    ) {
      toast("error", "VAT Inclusive & TDS Exclusive is not acceptable!");
      vatAmount = 0;
      tdsAmount = 0;
      vendorPayment = 0;
      costOfCompany = 0;
    }

    // -----------------------
    // Push values into form
    // -----------------------
    form.setFieldsValue({
      vat_amount: vatAmount.toFixed(2) || 0,
      tds_amount: tdsAmount.toFixed(2) || 0,
      payment_amount: vendorPayment.toFixed(2) || 0,
      cost_of_company: costOfCompany.toFixed(2) || 0,
    });
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedInvoiceAmount,
    watchedVatCondition,
    watchedVatPercentage,
    watchedTdsCondition,
    watchedTdsPercentage,
  ]);

  useEffect(() => {
    if (findVendor) {
      form.setFieldValue("vendor_code", findVendor?.vendor_code);
    }
  }, [form, findVendor]);

  useEffect(() => {
    if (defaultPaymentId) {
      form.setFieldValue("payment_to", defaultPaymentId);
    }
  }, [form, defaultPaymentId]);

  useEffect(() => {
    if (invoiceDetails) {
      const { payment_date, invoice_date, vendor, payment_to, ...rest } =
        invoiceDetails?.data || {};
      form.setFieldsValue({
        ...rest,
        payment_date: dayjs(payment_date),
        invoice_date: dayjs(invoice_date),
      });
      form.setFieldsValue({
        ...rest,
        payment_date: dayjs(payment_date),
        invoice_date: dayjs(invoice_date),
        vendor_code: vendor?.vendor_code,
        payment_to: payment_to?.payment_info_id,
      });
    }
  }, [form, invoiceDetails]);

  useEffect(() => {
    if ((created || updated) && !(creating || updating)) {
      navigate("/admin/invoices/list");
      toast(
        "success",
        isUpdating
          ? "Invoice created successfully."
          : "Invoice updated successfully.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, creating, updated, updating]);

  // ------ Errors ------
  useError(isCreateError, createError, "Invoice create failed.");
  useError(isUpdateError, updateError, "Invoice update failed.");

  return (
    <SidebarLayout title="Invoice Management">
      <TitleBar
        backLink="/admin/invoices/list"
        title="Add New Invoice"
        subTitle="Please enter your invoice information carefully"
      />

      <WithLoader isLoading={invoiceLoading || invoiceFetching}>
        <Form
          className="custom-form"
          layout="vertical"
          // requiredMark="optional"
          size="large"
          form={form}
          onFinish={handleFinish}
        >
          <Row gutter={24}>
            <Col span={16} className="space-y-4">
              {/* ------ Vendor Information ------ */}
              <Row
                gutter={14}
                className="p-5 rounded-xl border border-theme-indigo-50"
              >
                <Col className="!pl-0" span={12}>
                  <Form.Item
                    label="Vendor Name"
                    className="mb-4"
                    name="vendor_id"
                    rules={[
                      { required: true, message: "Please enter vendor name" },
                    ]}
                  >
                    <Select
                      showSearch
                      filterOption={selectSearchOption}
                      options={selectArrayOption(
                        vendorList?.data?.vendors || [],
                        "business_name",
                        "id",
                      )}
                      placeholder="Select vendor name"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Job Title"
                    className="mb-4"
                    name="job_title"
                    rules={[
                      { required: true, message: "Please enter job title" },
                    ]}
                  >
                    <Input placeholder="Enter job title" />
                  </Form.Item>

                  <Form.Item
                    label="Department"
                    className="mb-0"
                    name="department_id"
                    // rules={[
                    //   { required: true, message: "Please select department" },
                    // ]}
                  >
                    <Select
                      options={selectArrayOption(
                        departmentList?.data?.departments || [],
                        "name",
                        "id",
                      )}
                      placeholder="Select department"
                    />
                  </Form.Item>
                </Col>

                <Col className="!pr-0" span={12}>
                  <Form.Item
                    label="Vendor ID"
                    className="mb-4"
                    name="vendor_code"
                    rules={[
                      { required: true, message: "Please enter vendor ID" },
                    ]}
                  >
                    <Input disabled placeholder="Enter Vendor ID" />
                  </Form.Item>
                  <Form.Item
                    label="Business Unit"
                    className="mb-0"
                    name="business_unit"
                    rules={[
                      {
                        required: true,
                        message: "Please select business unit",
                      },
                    ]}
                  >
                    <Select
                      options={selectArrayOption(
                        businessUnistList?.data?.business_units || [],
                        "name",
                        "id",
                      )}
                      placeholder="Select business unit"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ------ Invoice Information ------ */}
              <Row
                gutter={14}
                className="p-5 rounded-xl border border-theme-indigo-50"
              >
                <Col className="!pl-0" span={12}>
                  <Form.Item
                    label="Invoice Date"
                    className="mb-4"
                    name="invoice_date"
                    rules={[
                      { required: true, message: "Please select invoice date" },
                    ]}
                  >
                    <DatePicker
                      className="w-full"
                      format={"DD MMM, YYYY"}
                      suffixIcon={<CalendarIcon />}
                      placeholder="dd/mm/yyy"
                      disabledDate={(current) => {
                        return current && current > dayjs().endOf("day");
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="PO Number (Optional)"
                    className="mb-0"
                    name="po_number"
                  >
                    <Input placeholder="Enter PO number" />
                  </Form.Item>
                </Col>
                <Col className="!pr-0" span={12}>
                  <Form.Item
                    label="Payment Terms [Days]"
                    className="mb-4"
                    name="payment_terms"
                    rules={[
                      { required: true, message: "Please enter payment terms" },
                      { validator: validatePositiveNumber },
                    ]}
                  >
                    <Input placeholder="d.g., Days" />
                  </Form.Item>
                  <Form.Item
                    label="Reference ID (Optional)"
                    className="mb-0"
                    name="reference_id"
                    rules={[
                      {
                        required: false,
                        message: "Please enter payment terms",
                      },
                      { validator: validateReferenceId },
                    ]}
                  >
                    <Input placeholder="Enter reference ID" />
                  </Form.Item>
                </Col>
              </Row>

              {/* ------ Documents ------ */}
              <InvoiceDocuments form={form} />

              {/* ------ Payment Information ------ */}
              <Row
                gutter={14}
                className="p-5 rounded-xl border border-theme-indigo-50"
              >
                <Col span={24} className="!px-0 mb-4">
                  <h5 className="text-lg text-theme-gray-2 font-semibold">
                    Payment Information
                  </h5>
                </Col>
                <Col className="!pl-0 space-y-4" span={6}>
                  <span className="h-[44px] flex items-center text-sm font-medium text-theme-gray-2">
                    Payment From *
                  </span>
                  <span className="h-[44px] flex items-center text-sm font-medium text-theme-gray-2">
                    Payment Account *
                  </span>
                  <span className="h-[44px] flex items-center text-sm font-medium text-theme-gray-2">
                    Payment Date *
                  </span>
                </Col>
                <Col className="!pr-0" span={18}>
                  <Form.Item
                    className="mb-4"
                    name="payment_from"
                    rules={[
                      { required: true, message: "Please select payment from" },
                    ]}
                  >
                    <Select
                      options={[
                        {
                          label: "Company Account - DBBL",
                          value: "Company Account - DBBL",
                        },
                      ]}
                      placeholder="Select payment from"
                    />
                  </Form.Item>
                  <Form.Item
                    className="mb-4"
                    name="payment_to"
                    rules={[
                      {
                        required: true,
                        message: "Please enter payment account",
                      },
                    ]}
                  >
                    <Select
                      disabled={isLoading || isFetching || !watchedVendorID}
                      options={paymentOptions || []}
                      placeholder="Select account"
                    />
                  </Form.Item>
                  <Form.Item
                    className="mb-0"
                    name="payment_date"
                    rules={[
                      { required: true, message: "Please enter payment date" },
                    ]}
                  >
                    <DatePicker
                      className="w-full"
                      format={"DD MMM, YYYY"}
                      suffixIcon={<CalendarIcon />}
                      placeholder="dd/mm/yyy"
                      disabledDate={(current) =>
                        current && current < dayjs().startOf("day")
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            {/* ------ Information Calculation ------ */}
            <Col span={8}>
              <Row
                gutter={14}
                className="p-5 rounded-xl border border-theme-indigo-50 bg-theme-primaryRest"
              >
                <Col className="!pr-0" span={24}>
                  <Form.Item
                    label="Invoice Amount (BDT)"
                    className="mb-4"
                    name="invoice_amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter invoice amount",
                      },
                      { validator: validatePositiveNumber },
                    ]}
                  >
                    <InputNumber
                      formatter={formatNumberWithComma}
                      parser={parseNumberFromComma}
                      placeholder="00"
                      className="bgwhite !w-full"
                    />
                  </Form.Item>
                </Col>
                <Col className="!pr-0" span={12}>
                  <Form.Item
                    label="VAT Condition"
                    className="mb-4"
                    name="vat_condition"
                    rules={[
                      {
                        required: Boolean(vatPercentage || tdsPercentage),
                        message: "Please select VAT condition",
                      },
                    ]}
                  >
                    <Select
                      options={[
                        { value: "exclusive", label: "Exclusive" },
                        { value: "inclusive", label: "Inclusive" },
                      ]}
                      placeholder="Select"
                      className="bgwhite"
                      onChange={() =>
                        form.resetFields([
                          "tds_condition",
                          "vat_amount",
                          "tds_amount",
                          "payment_amount",
                          "cost_of_company",
                        ])
                      }
                    />
                  </Form.Item>
                </Col>
                <Col className="!pr-0" span={12}>
                  <Form.Item
                    label="TDS Condition"
                    className="mb-4"
                    name="tds_condition"
                    rules={[
                      {
                        required: Boolean(vatPercentage || tdsPercentage),
                        message: "Please select TDS condition",
                      },
                    ]}
                  >
                    <Select
                      options={[
                        {
                          value: "exclusive",
                          label: "Exclusive",
                          disabled: watchedVatCondition === "inclusive",
                          title:
                            "* VAT Inclusive & TAX Exclusive is not acceptable",
                        },
                        { value: "inclusive", label: "Inclusive" },
                      ]}
                      placeholder="Select"
                      className="bgwhite"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} className="!pr-0">
                  <Form.Item
                    label="VAT"
                    rootClassName="mb-4 label-center"
                    name="vat_percentage"
                    initialValue={0}
                    layout="horizontal"
                    rules={[
                      { required: true, message: "Please enter VAT" },
                      { validator: validatePositiveNumber },
                    ]}
                  >
                    <InputNumber
                      controls={false}
                      placeholder="00"
                      className="bgwhite w-full"
                      suffix={<PercentageIcon />}
                      formatter={formatNumberWithComma}
                      parser={parseNumberFromComma}
                      max={100}
                      precision={2}
                      onChange={(value) => {
                        handlePercentageChange(value as number, "vat");
                        if (!value || Number(value) < 1) {
                          form.resetFields([
                            "vat_amount",
                            "tds_amount",
                            "payment_amount",
                            "cost_of_company",
                          ]);
                        }
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="TDS"
                    rootClassName="mb-4 label-center"
                    name="tds_percentage"
                    layout="horizontal"
                    initialValue={0}
                    rules={[
                      { required: true, message: "Please enter TDS" },
                      { validator: validatePositiveNumber },
                    ]}
                  >
                    <InputNumber
                      controls={false}
                      placeholder="00"
                      className="bgwhite w-full"
                      suffix={<PercentageIcon />}
                      formatter={formatNumberWithComma}
                      parser={parseNumberFromComma}
                      max={100}
                      precision={2}
                      onChange={(value) => {
                        handlePercentageChange(value as number, "tds");
                        if (!value || Number(value) < 1) {
                          form.resetFields([
                            "vat_amount",
                            "tds_amount",
                            "payment_amount",
                            "cost_of_company",
                          ]);
                        }
                      }}
                    />
                  </Form.Item>
                  <span className="h-[44px] flex items-center text-sm font-medium text-theme-gray-2 mb-4">
                    Vendor Payment:
                  </span>
                  <span className="h-[44px] flex items-center text-sm font-medium text-theme-gray-2">
                    Cost of Company:
                  </span>
                </Col>
                <Col span={12} className="!pr-0">
                  <Form.Item
                    initialValue={0}
                    className="mb-4"
                    name="vat_amount"
                  >
                    <InputNumber
                      readOnly
                      controls={false}
                      prefix="BDT"
                      placeholder="00"
                      className="bgwhite w-full alignment-right"
                      formatter={formatNumberWithComma}
                      parser={parseNumberFromComma}
                    />
                  </Form.Item>
                  <Form.Item
                    initialValue={0}
                    className="mb-4"
                    name="tds_amount"
                  >
                    <InputNumber
                      readOnly
                      controls={false}
                      prefix="BDT"
                      placeholder="00"
                      className="bgwhite w-full alignment-right"
                      formatter={formatNumberWithComma}
                      parser={parseNumberFromComma}
                    />
                  </Form.Item>
                  <Form.Item className="mb-4" name="payment_amount">
                    <InputNumber
                      readOnly
                      controls={false}
                      prefix="BDT"
                      placeholder="00"
                      className="bgwhite w-full alignment-right"
                      formatter={formatNumberWithComma}
                      parser={parseNumberFromComma}
                    />
                  </Form.Item>
                  <Form.Item className="mb-4" name="cost_of_company">
                    <InputNumber
                      readOnly
                      controls={false}
                      prefix="BDT"
                      placeholder="00"
                      className="bgwhite w-full alignment-right"
                      formatter={formatNumberWithComma}
                      parser={parseNumberFromComma}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ------ Form Submit ------ */}
              <Flex justify="end" className="mt-4" gap={12}>
                <Button
                  onClick={() => navigate("/admin/invoices/list")}
                  htmlType="button"
                  size="small"
                  className="button-white-bordered min-w-[100px]"
                >
                  Cancel
                </Button>
                <Button
                  loading={creating || updating}
                  htmlType="submit"
                  size="small"
                  className="button-primary min-w-[100px]"
                >
                  {isUpdating ? "Update" : "Save"}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </WithLoader>
    </SidebarLayout>
  );
};

export default ManageInvoice;
